'use client';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Loading from '../core/Loading';
import TitleBar from '../layout/TitleBar';
import TomsVendingSiteVisitSection, {
  type TomsVendingSiteVisitSectionHandle,
} from '../toms-vending/TomsVendingSiteVisitSection';
import TomsVendingSiteVisitCashSection from '../toms-vending/TomsVendingSiteVisitCashSection';
import { useTomVendingLocationDetails } from '@/hooks/useTomVendingLocation';
import { useTomVendingMachinesList } from '@/hooks/useTomVendingMachine';
import {
  finalizeTomVendingSiteVisit,
  startOrGetTomVendingSiteVisit,
} from '@/hooks/useTomVendingSiteVisit';
import { useNotification } from '@/contexts/NotificationContext';

const TomsVendingLocationSiteVisitPage: React.FC = () => {
  const { locationId: locationIdParam } = useParams();
  const locationId = parseInt(String(locationIdParam ?? ''), 10);
  const { showError, showSuccess } = useNotification();

  const [siteVisitId, setSiteVisitId] = useState<number | null>(null);
  const [siteVisitStatus, setSiteVisitStatus] = useState<string>('open');
  const [visitSessionError, setVisitSessionError] = useState<string | null>(null);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const tallyRef = useRef<TomsVendingSiteVisitSectionHandle | null>(null);

  if (!locationIdParam || Number.isNaN(locationId) || locationId <= 0) {
    return <div>Invalid location</div>;
  }

  const { data, isLoading, error } = useTomVendingLocationDetails(locationIdParam);
  const queryClient = useQueryClient();
  const { data: machines = [] } = useTomVendingMachinesList(locationId);
  const sortedMachines = useMemo(
    () => [...machines].sort((a, b) => a.sortOrder - b.sortOrder || a.assetId.localeCompare(b.assetId)),
    [machines],
  );

  useEffect(() => {
    let cancelled = false;
    async function bootSession() {
      try {
        setVisitSessionError(null);
        const { siteVisit } = await startOrGetTomVendingSiteVisit(locationId);
        if (!cancelled) {
          setSiteVisitId(siteVisit.id);
          setSiteVisitStatus(siteVisit.visitStatus ?? 'open');
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setSiteVisitId(null);
          setVisitSessionError(e instanceof Error ? e.message : 'Could not start site visit session');
        }
      }
    }
    void bootSession();
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  const handleFinalize = async () => {
    if (siteVisitId == null || siteVisitId <= 0) {
      showError('Site visit session not ready — refresh this page.');
      return;
    }
    setFinalizing(true);
    try {
      const flushed = await tallyRef.current?.flushPendingSaves();
      if (flushed === false) {
        setFinalizeOpen(false);
        return;
      }
      const sv = await finalizeTomVendingSiteVisit(siteVisitId);
      setSiteVisitStatus(sv.visitStatus ?? 'finalized');
      setFinalizeOpen(false);
      showSuccess('Visit finalized. Fill quantities have left warehouse inventory.');
      void queryClient.invalidateQueries({ queryKey: ['tom-vending-location-details', locationIdParam] });
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to finalize visit');
    } finally {
      setFinalizing(false);
    }
  };

  if (isLoading) {
    return <Loading loading={true} noBackdrop />;
  }
  if (error || !data?.location) {
    return <div>Error loading location</div>;
  }

  const location = data.location;
  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Locations', href: '/app/toms-vending/locations' },
    { label: location.name, href: `/app/toms-vending/locations/${locationId}` },
    { label: 'Site visit' },
  ];
  const visitOpen = siteVisitStatus === 'open' && siteVisitId != null && !visitSessionError;

  return (
    <>
      <TitleBar breadcrumbs={breadcrumbs} title={`Site visit — ${location.name}`} />
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
          <Button
            component={RouterLink}
            to={`/app/toms-vending/locations/${locationId}`}
            startIcon={<ArrowBackIcon />}
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Back to location
          </Button>
          <Box sx={{ flex: 1 }} />
          {visitOpen ? (
            <Button
              variant="contained"
              onClick={() => setFinalizeOpen(true)}
              disabled={finalizing}
              sx={{ textTransform: 'none' }}
            >
              Finalize visit
            </Button>
          ) : siteVisitStatus === 'finalized' ? (
            <Typography variant="body2" color="text.secondary">
              Visit finalized — tallies are read-only.
            </Typography>
          ) : null}
        </Box>
        {visitSessionError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {visitSessionError} — saving tallies is disabled until this succeeds.
          </Alert>
        )}
        <TomsVendingSiteVisitCashSection
          siteVisitId={siteVisitId}
          visitStatus={siteVisitStatus}
          machines={sortedMachines}
        />
        <TomsVendingSiteVisitSection
          ref={tallyRef}
          siteVisitId={siteVisitId}
          visitStatus={siteVisitStatus}
          locationId={locationId}
          machines={sortedMachines}
          onApplied={() => {
            void queryClient.invalidateQueries({ queryKey: ['tom-vending-location-details', locationIdParam] });
          }}
        />
      </Box>
      <Dialog open={finalizeOpen} onClose={() => !finalizing && setFinalizeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Finalize this visit?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Fill quantities you recorded are taken from warehouse inventory and added to this site now.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            After you tap Okay, the visit closes and tallies become read-only.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={finalizing} onClick={() => setFinalizeOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" disabled={finalizing} onClick={() => void handleFinalize()}>
            {finalizing ? 'Finalizing…' : 'Okay'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TomsVendingLocationSiteVisitPage;
